import React from "react";

import { useParams } from "react-router-dom";
import { sections } from "@/modules/performance/feedbacks/feedback/constants";
import MultiRingProgress from "@/modules/performance/components/multi-ring-progress";
import { EvtHeader } from "components/header";
import Legend from "@/modules/performance/components/legend";
import { useAverageFeedback } from "@/modules/performance/average-feedback/use-average-feedback";
import EmployeeSection from "@/modules/performance/components/section/employee";
import { useSelector } from "react-redux";
import { selectUser } from "store/src/slices/auth-slice";

// Tailwind sky palette equivalents

const COLORS = {
  self: "#bae6fd", // sky-200
  team: "#7dd3fc", // sky-300
  grade: "#38bdf8", // sky-400
  reviewers: "#0ea5e9", // sky-500
};

const AverageFeedback = ({ showEmployee = false }) => {
  const user = useSelector(selectUser);
  const { quadId, employeeId } = useParams();
  const { isLoading, isError, getMetric, employee } = useAverageFeedback(
    quadId,
    employeeId || user.id,
  );

  if (isLoading) return <div className="p-6">Loading average feedback...</div>;
  if (isError)
    return (
      <div className="p-6 text-red-500">Failed to load average feedback.</div>
    );

  return (
    <div className="space-y-4">
      <EvtHeader title="Average Feedback">
        <Legend
          items={[
            { color: COLORS.self, label: "Self Review" },
            { color: COLORS.team, label: "Team Average" },
            { color: COLORS.grade, label: "Grade Average" },
            { color: COLORS.reviewers, label: "Reviewer's Average" },
          ]}
        />
      </EvtHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-4 lg:px-6">
        {showEmployee && (
          <EmployeeSection
            title="Employee"
            employee={employee}
            className="col-start-3"
          />
        )}
      </div>
      <div className="space-y-10">
        {sections.map((section, idx) => (
          <div key={section.title} className="px-4 md:px-6">
            {idx !== 0 && (
              <hr className="border-dashed border-t border-zinc-200 my-6" />
            )}
            <h3 className="text-base font-semibold text-foreground mb-6">
              {section.title}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
              {section.items.map((item) => {
                const v = getMetric(item.key);
                const rings = [
                  { value: v.reviewers, color: COLORS.reviewers },
                  { value: v.grade, color: COLORS.grade },
                  { value: v.team, color: COLORS.team },
                  { value: v.self, color: COLORS.self },
                ];
                return (
                  <div
                    key={item.key}
                    className="flex flex-col items-center gap-3"
                  >
                    <MultiRingProgress
                      values={rings}
                      size={200}
                      stroke={10}
                      gap={8}
                    />
                    <div className="text-sm text-center text-muted-foreground max-w-40">
                      {item.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AverageFeedback;
